// Per-game panel content for board-othellos (돌의 순례).
// The shell (src/gamzone/) and layout (App.tsx) are the shared template;
// this file + src/gameId.ts + src/game/ are the only game-specific parts.

import { evt } from "../gameId.ts";
import { isDevMode } from "../gamzone/config.ts";
import type { LangCode } from "../gamzone/i18n.ts";

// The campaign is complete (15/15 stages) — this is the normal build now.
// QA-only conveniences (the ▶◀▼ outcome-forcing d-pad *clicks*, the 다시
// 시작/처음으로 panel links, the old "스테이지 1만 포함" disclaimer) are gated
// behind `?dev` (see gamzone/config.ts's `isDevMode`) instead of shown to
// every player. The d-pad's ▲◀▶▼ *glyphs* stay visible either way — matching
// the other Gamzone games (e.g. 2026 Galaga), where the control legend is
// standard chrome, not a test-only element. Add `?dev` to the URL to get the
// hidden pieces back.
export const DEV = isDevMode();

export const gameConfig = {
  title: "돌의 순례",
  subtitle: "PILGRIMAGE OF STONES",

  /** PC/모바일 조작법이 실제로 다르므로(PC: 방향키+Enter, 모바일: 보드 탭)
   *  isMobile을 받는 함수로 둔다 — 2026/Coin의 helpControlsPc /
   *  helpControlsMobile* 분기와 같은 패턴. App.tsx가 useIsMobile()로 호출.
   *  `lang`도 함께 받아 규칙 설명을 언어별로 렌더링한다. */
  helpHtml(isMobile: boolean, lang: LangCode): string {
    if (lang === "en") {
      return (
        (isMobile
          ? "<p><strong>Controls</strong> Tap the board to place a disc</p>"
          : "<p><strong>Controls</strong> ← ↑ → ↓ move the cursor · <b>Enter</b> to place · mouse click also works</p>") +
        "<p><strong>Rules</strong> Othello/Reversi. The board starts with 2 black and 2 white discs " +
        "placed diagonally in the center; black moves first.</p>" +
        "<p>You may only place a disc where it traps one or more opposing discs in a straight " +
        "line (horizontal, vertical, or diagonal); every trapped disc then flips to your color.</p>" +
        "<p>If you have no legal move, your turn is skipped. The game ends when neither side has " +
        "a move or all 64 squares are filled — whoever has more discs wins, or it's a draw if equal.</p>"
      );
    }
    return (
      (isMobile
        ? "<p><strong>조작</strong> 보드를 탭하여 착수합니다</p>"
        : "<p><strong>조작</strong> ← ↑ → ↓ 커서 이동 · <b>Enter</b> 착수 · 마우스 클릭도 가능</p>") +
      "<p><strong>규칙</strong> 오델로·리버시. 중앙에 흑돌 2개, 백돌 2개가 " +
      "대각선으로 엇갈려 놓인 채 시작하고, 흑(선)이 먼저 둡니다.</p>" +
      "<p>상대 돌을 가로·세로·대각선 일직선으로 내 돌 사이에 끼울 수 있는 " +
      "칸에만 둘 수 있고, 두면 그 사이에 낀 상대 돌이 전부 내 돌로 뒤집힙니다.</p>" +
      "<p>둘 곳이 없으면 차례를 건너뛰고, 양쪽 다 둘 곳이 없거나 64칸이 모두 " +
      "채워지면 게임이 끝납니다. 이때 돌이 더 많은 쪽이 승리하고, 같으면 " +
      "무승부입니다.</p>"
    );
  },

  /** PC control parity with the other Gamzone games: arrow keys move the board
   *  cursor, Enter places. (Mouse click still works.) The on-screen ▲◀▶▼ d-pad
   *  is always shown, for the same reason every other Gamzone game shows one —
   *  it's the control legend, not a test element. Only its ◀▶▼ *click*
   *  behaviour is DEV-only (App.tsx guards the handlers): outside `?dev` they
   *  do nothing, so a normal player clicking them just gets an inert legend. */
  controls: {
    show: true,
    dpad: true,
    dpadGlyphs: ["▲", "◀", "▶", "▼"] as [string, string, string, string],
    actionLabel(lang: LangCode): string[] {
      return [lang === "en" ? "Place" : "착수"];
    },
  },

  // DEV-only panel buttons: "다시 시작" replays the current 막 from its opening
  // narration; "처음으로" wipes all progress and replays from the 막 I prologue.
  ctaLabel(lang: LangCode): string | null {
    if (!DEV) return null;
    return lang === "en" ? "Restart" : "다시 시작";
  },
  resetLabel(lang: LangCode): string | null {
    if (!DEV) return null;
    return lang === "en" ? "Reset" : "처음으로";
  },
  showAdSlot: true,

  // No point reward for this build — win or lose. "" hides the policy line.
  rewardLine: "" as string,
  rewardPoints: 0,
} as const;

/** Fired by the ⚙ options button that campaign screens (map/dialogue/
 *  subtitles/ending — see OptionsButton.tsx) show in the corner, since only
 *  MatchScreen has its own on-canvas pause/settings access otherwise.
 *  App.tsx listens and opens the same Settings modal as the sidebar's
 *  account button. */
export const OPTIONS_EVENT = evt("options");

/** window event the panel CTA fires; Game.tsx listens — "다시 시작" replays the
 *  current 막 from its opening narration. */
export const CTA_EVENT = evt("cta");

/** TEST build: the "처음으로" button fires this; Game.tsx wipes all progress and
 *  replays from the 막 I prologue. */
export const RESET_EVENT = evt("reset");

/** TEST build only: fired by clicking a d-pad glyph in the control legend, so
 *  the loss / draw result screens are easy to check without playing them out.
 *  MatchScreen listens: ▶ skip as a win, ◀ force a loss, ▼ force a draw. */
export const SKIP_EVENT = evt("skip");
export const LOSE_EVENT = evt("lose");
export const DRAW_EVENT = evt("draw");
